import { useState, useEffect, type CSSProperties, useCallback } from 'react';

import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { getGridProductsById } from 'actions/products';

import { useUser } from 'context/user';
import { useI18n } from 'context/i18n';
import { useProductsGrid } from 'context/products-grid';

import { isDomain } from 'utils/domain';

import { parseProductClickEcommerceGA4 } from 'utils/gtmUtils';
import { getRealID, handleStorage, COOKIES, getMarket, getLang, getCookie, eraseCookie, setCookie, obtainVoucherPrices, getFixedPrice } from 'utils/helpers';

import { MAX_BAG_LENGTH } from 'utils/constants/products';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { dataLayerHandleEvent, dataLayerCartClick } from 'utils/dataLayers';
import { setBagCookies } from 'utils/bag';

import { addToBag } from 'actions/products';

import { saveLastViewedProduct } from 'utils/grid';
import { getDomainValue } from 'utils/domain';
import { useLocale } from 'hooks/useLocale';
import { ProductCardSizes } from './product-card-sizes';
import { ProductCardMedia } from './product-card-media';
import { useFeature } from 'context/feature-flags';
import { Skeleton } from 'components/ui/skeleton';

import type { TGridProduct } from 'types/product-list';

import styles from './style.module.css';
import { tryCatch } from 'utils/common';

const ProductBrands = dynamic(() => import('components/productPurchaseCol/product-brands').then((mod) => mod.ProductBrands));
const AddToBagDialog = dynamic(() => import('components/product/add-to-bag-dialog').then((mod) => mod.AddToBagDialog));

export type ProductCardProps = {
  item?: TGridProduct;
  loadingProp?: any;
  // colXS: Record<string, any>;
  // colMD: Record<string, any>;
  // colLG: Record<string, any>;
  // colXL: Record<string, any>;
  // colXXL: Record<string, any>;
  withHover: boolean;
  withLabel: boolean;
  // defaultLimit?: number; // No usado actualmente
  // isLastProduct?: boolean; // No usado actualmente
  imagesHover?:
    | {
        xs?: string;
        md?: string;
        lg?: string;
        full?: string;
      }
    | undefined;
  currentList?: string;
  // currentListName?: string; // No usado actualmente
  camperlab: boolean;
  newShooting: boolean;
  withSKU: boolean;
  withDatalayerClick?: boolean;
  children?: any;
  index?: number;
  recalculating?: boolean;
  // hasModal: boolean; // No usado actualmente
  squareImg: boolean;
  showPrices?: boolean;
  saveLastProduct: boolean;
  carousel?: boolean;
  addToBagPopup?: boolean;
  contentClassName?: string;
  imagesClassName?: string;
  canShowSizes?: boolean;
  changedItemId?: ((newItemId: string) => void) | undefined;
  slider?: boolean;
};

type ProductAnchorElementProps =
  | ({
      tag: 'a';
    } & React.ComponentPropsWithoutRef<'a'>)
  | ({
      tag: 'div';
    } & React.ComponentPropsWithoutRef<'div'>);

function ProductElement(props: ProductAnchorElementProps) {
  if (props.tag === 'div') {
    return <div {...props} />;
  }
  return <a {...props} />;
}

export function ProductCard({
  item: originalItem = {} as TGridProduct,
  withHover = true,
  withLabel = false,
  imagesHover: initialImagesHover,
  currentList,
  camperlab = false,
  newShooting = false,
  withSKU = false,
  withDatalayerClick = true,
  children,
  index = 0,
  recalculating = false,
  loadingProp = 'lazy',
  squareImg = false,
  showPrices = true,
  saveLastProduct = false,
  carousel = false,
  addToBagPopup = false,
  contentClassName = '',
  imagesClassName = '',
  canShowSizes = true,
  changedItemId,
  slider = false,
}: ProductCardProps) {
  const { query } = useRouter();
  const { locale } = useLocale();
  const { t, profileData, bagLength, setBag, setBagLength, bag, setPromoLabel, i18nPage, giftCardList, physicalGiftCardList } = useI18n();
  const canFetchColors = useFeature('fetch_colors');
  const { getSingleProductData, addProductsData, isProductInMap } = useProductsGrid();

  // la información siempre vendrá de getProduct con el itemID que tengamos actualmente
  const [itemID, setItemID] = useState<string | undefined>(() => originalItem?.id);

  useEffect(() => {
    if (changedItemId) {
      changedItemId(itemID ?? '');
    }
  }, [itemID, changedItemId]);

  const item = getSingleProductData({ id: itemID ?? originalItem.id, defaultProduct: originalItem });
  const { prices, sizes, id, name } = item;
  let url = item.url;

  const imagesHover = itemID === originalItem.id ? initialImagesHover : undefined;

  const { config } = profileData;
  const showFreeShipping = config?.show_free_shipping || false;
  const showGiftPurchase = config?.show_gift_purchase || false;
  const { shouldRecalculateVouchers, contextFilters, activatedPrevouchers, handleActivatePrevoucher, initialAppliedVoucher } = useUser();
  const [hoveredSize, setHoveredSize] = useState<string | null>(null);
  const [isCalculating, setIsCalculating] = useState(recalculating);
  const isGiftCard = giftCardList.includes(id) || physicalGiftCardList.includes(id);

  // show colors
  const [voucherApplied, setVoucherApplied] = useState<boolean>(!!initialAppliedVoucher);
  const productFamily = getRealID('product', query.product);
  const realTargetId = getRealID('target', query.target);
  const showColors =
    getDomainValue({ camper: true, nnormal: true, camperlab: false }) && productFamily !== '6' && originalItem?.colors && originalItem.colors.length > 1;

  const [currentPriceData, setCurrentPriceData] = useState(prices !== null && prices !== undefined ? prices : {});
  const [voucherPriceText, setVoucherPriceText] = useState(null);
  // const [loadingSizesError, setLoadingSizesError] = useState(null);
  // const [itemSizes, setItemSizes] = useState(null);
  // const [isAddingToBag, setIsAddingToBag] = useState(false);
  const [touristMode, setTouristMode] = useState(false);
  const [showingAddToBagPopup, setShowingAddToBagPopup] = useState(false);

  // const isMobile = getIsMobile('767.5px');
  // const isTablet = getIsMobile('1280px');

  const { previous, discount, currentFormated, previousFormated, installments, discountStr } = currentPriceData;

  if (!url) {
    url = `/${locale}/producto?option=${id}`;
  }
  const paths = url ? url.split('/') : [];
  if (paths.length > 4 && (paths[3]?.indexOf('_') ?? -1) > -1) {
    url = url.replace(paths[3] as string, locale);
  }
  // const formatedUrl = url ? `/${url.split(`/${locale}/`)[1]}` : '#';
  const formatedUrl = url ? url.substring(url.indexOf(`/${locale}/`) + 6) : '#';

  const hasSizes = sizes && sizes.length > 0;
  const outOfStock = (hasSizes && sizes?.every((size) => size.available === false)) || item?.notifyMeType === 'OUT';
  const showSizes = canShowSizes && hasSizes && !outOfStock;
  // const { storeId } = profileData;
  const market = getMarket(locale);

  const calculateVoucherPrice = useCallback(() => {
    if (typeof window === undefined) return;
    const countryPrice = getCookie(COOKIES.COUNTRY_PRICE);
    if (countryPrice && countryPrice !== market) {
      setTouristMode(true);
    }
    const voucherPriceInfo = obtainVoucherPrices(item, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher);
    if (voucherPriceInfo !== null) {
      setIsCalculating(true);
      const { message, info } = voucherPriceInfo;

      if (message) {
        setVoucherPriceText(message);
      } else {
        const infoPriceData = { ...info };
        // caso raro en el buscador
        if (
          infoPriceData.pricePerSize &&
          !infoPriceData?.priceDiscountFromFormatted &&
          !infoPriceData?.priceDiscountToFormatted &&
          infoPriceData?.voucherRangePrices?.priceFromFormatted
        ) {
          infoPriceData.priceDiscountFromFormatted = infoPriceData?.voucherRangePrices?.priceFromFormatted;
          infoPriceData.priceDiscountToFormatted = infoPriceData?.voucherRangePrices?.priceToFormatted;
          infoPriceData.priceFromFormatted = prices?.priceFromFormatted;
          infoPriceData.priceToFormatted = prices?.priceToFormatted;
          if (prices.usePreviousMinAndMaxPrice) {
            infoPriceData.previousFromFormatted = prices?.previousFromFormatted;
            infoPriceData.previousToFormatted = prices?.previousFromFormatted;
          }
        }
        setCurrentPriceData({ ...infoPriceData, discountFromVoucher: true });
        setVoucherApplied(true as TODO);
        setVoucherPriceText(null);
      }
    } else {
      setVoucherApplied(false as TODO);
      setVoucherPriceText(null);
    }

    setTimeout(() => {
      setIsCalculating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (shouldRecalculateVouchers) {
      calculateVoucherPrice();
    }
  }, [shouldRecalculateVouchers, calculateVoucherPrice]);

  useEffect(() => {
    setCurrentPriceData(prices !== null && prices !== undefined ? prices : {});
    calculateVoucherPrice();
  }, [prices, calculateVoucherPrice]);

  useEffect(() => {
    if (recalculating !== isCalculating) {
      setIsCalculating(recalculating);
    }
  }, [recalculating]);

  const onClickProduct = () => {
    if (withDatalayerClick) {
      const eventHandler = {
        You_may_also_be_interested: { eventAct: 'youMightLikeProductClick' },
        Recently_viewed: { eventAct: 'recentlyViewedProductClick' },
      };

      const handlerParams = eventHandler[(currentList as keyof typeof eventHandler) ?? 'Recently_viewed'];
      dataLayerHandleEvent({ ...handlerParams });

      const previousSelectItemData = handleStorage('getItem', 'gtmSelectData');
      let newSelectData = null;

      const previousDataParsed: Record<string, any> = previousSelectItemData === undefined ? {} : (JSON.parse(previousSelectItemData) as Record<string, any>);

      newSelectData = { ...previousDataParsed, [item.id]: parseProductClickEcommerceGA4(item, (index ?? 0) + 1)[0] } as Record<string, any>;

      handleStorage('setItem', 'gtmSelectData', JSON.stringify(newSelectData));

      const baseEventGA4 = {
        event: 'select_item',
        ecommerce: { items: parseProductClickEcommerceGA4(item, (index ?? 0) + 1) },
        isLab: camperlab || false,
      } as any;
      dataLayerHandleEvent({ ...baseEventGA4, ...handlerParams });
    }
  };

  // const handleLoadProductSizes = async () => {
  //   try {
  //     const response = await getProductLive({ context: undefined, lang: locale, target: item.targetId, productCode: item.id });
  //     const productSizes = response?.productSheetLive?.sizes;

  //     setItemSizes(productSizes);
  //   } catch (e) {
  //     console.error(e);
  //     setLoadingSizesError('Error.');
  //   }
  // };

  const addToCartHandler = async ({ payload, cookiesBagId, showJustAdded }: { payload: TODO; cookiesBagId: string | null; showJustAdded: boolean }) => {
    const cookiesMarket = getCookie(COOKIES.BAG_MARKET);
    const cookiesShipment = getCookie(COOKIES.SHIPMENT);
    const cookiesBonds = getCookie(COOKIES.BONDS);
    const cookiesZip = getCookie(COOKIES.ZIP);
    const cookiesEmployeeId = getCookie(COOKIES.EMPLOYEE_ID);
    const cookiesPackstation = getCookie(COOKIES.PACKSTATION);
    const cookiesCtr = getCookie(COOKIES.IS_CTR);
    if (cookiesMarket !== market) {
      payload.hashOrderId = '';
      if (cookiesBagId) {
        eraseCookie(COOKIES.BAG);
        eraseCookie(COOKIES.BAG_DATE);
      }
      if (cookiesZip) {
        eraseCookie(COOKIES.ZIP);
      }
      if (cookiesShipment) {
        eraseCookie(COOKIES.SHIPMENT);
      }
      if (cookiesMarket !== null && cookiesBonds) {
        eraseCookie(COOKIES.BONDS, COOKIES.DOMAIN);
        eraseCookie(COOKIES.AUTO_BONDS);
      }
      if (cookiesEmployeeId) {
        eraseCookie(COOKIES.EMPLOYEE_ID);
      }
      if (cookiesPackstation) {
        eraseCookie(COOKIES.PACKSTATION);
      }
      if (cookiesCtr) {
        eraseCookie(COOKIES.IS_CTR);
      }
    }
    let updatedPromoLabel = null;
    const result = await addToBag(payload);
    const pricesForAdd = Object.keys(currentPriceData)?.length > 0 ? currentPriceData : item?.prices;
    const productSheet = {
      ...item,
      target: item.targetId,
      size: payload.size,
      prices: { ...pricesForAdd, currency: profileData?.currencySap, currencySap: profileData?.currencySap },
    };
    dataLayerCartClick({ ...payload, productSheet }, result, false, bag);
    if (result.hashOrderId) {
      setBagCookies({ hash: encodeURI(result.hashOrderId), market: market ?? getMarket(locale) });

      const { sapActive, extendedBlockSAP, shoppingCart } = result;
      if (!sapActive && !extendedBlockSAP) {
        // can't pay
        window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
      } else {
        if (extendedBlockSAP) {
          // can pay
          setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
        }
        if (!showJustAdded) {
          window.location.assign(`${window.location.origin}/${locale}/bolsa`);
        }
        if (showJustAdded) {
          const { price } = shoppingCart[0]; // siempre es una array con un solo item
          const newBag = bag ? JSON.parse(JSON.stringify(bag)) : [];
          setBag([{ ...result.shoppingCart[0], membersOnly: item?.membersOnly }, ...newBag]);
          setBagLength(bagLength + 1);
          // const style: CSSProperties = { width: !isMobile ? '360px' : '100%', maxWidth: !isMobile ? '360px' : '100vw', right: '-24px' };
          // if (isMobile) {
          //   style.minWidth = 'calc(100vw - 17px)';
          // }

          if (showFreeShipping) {
            updatedPromoLabel = result?.freeShippingLabel || null;
          }
          if (showGiftPurchase) {
            updatedPromoLabel = result?.gwplabel || result?.freeShippingLabel || null;
          }
          let priceDiscountString = '';
          if (discountStr || price?.discount) {
            priceDiscountString = discountStr || `-${price.discount}%`;
          }
          const priceDiscountSplitted = priceDiscountString?.split('-');
          if (priceDiscountSplitted?.length > 2) {
            const priceText = priceDiscountSplitted.pop();
            priceDiscountString = `-${priceText}`;
          }

          setPromoLabel(updatedPromoLabel);
        }
      }
    } else {
      // eslint-disable-next-line no-alert
      alert('No se puede procesar el pedido en este momento');
    }
  };

  const handleClickAddToCartBtn = async (showJustAdded = true, selectedSizeValue: string, selectedSizeLabel: string, payloadOverride = {} as TODO) => {
    /* const isUserIdentified = getCookie(COOKIES.SELLIGENT_USRHASH) || userIdentified || bagHasMembersOnlyProduct || userData !== null; */
    // setIsAddingToBag(true);
    if (bagLength >= MAX_BAG_LENGTH) {
      //TODO: poner notificacion de error (toast)
      return;
    }
    const cookiesBagId = getCookie(COOKIES.BAG);
    const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const voucherDiscount = IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
    const pricesForAdd = Object.keys(currentPriceData)?.length > 0 ? currentPriceData : item?.prices;
    let payload: any = {
      id,
      price: pricesForAdd,
      profile: { countryId: getMarket(locale), languageId: getLang(locale) },
      size: selectedSizeValue,
      sizeLabel: selectedSizeLabel,
      gender: item?.targetId || '',
      thumbnail: item?.imageBag || '', // en ocasiones queda como null y causa un error
    };
    if (voucherDiscount) {
      payload.voucher = voucher;
    }
    if (cookiesBagId) {
      payload.hashOrderId = decodeURI(cookiesBagId);
    }

    if (payloadOverride) {
      payload = { ...payload, ...payloadOverride };
    }

    await addToCartHandler({ payload, cookiesBagId, showJustAdded });
    // setIsAddingToBag(false);
  };

  const fetchColors = async (brandProductID: string, colorIDs: string[]) => {
    const alreadyHasData = isProductInMap({ id: brandProductID });

    if (alreadyHasData) {
      setItemID(brandProductID);
    } else {
      const { data: productResponse, error } = await tryCatch(
        getGridProductsById({ profile: locale, target: realTargetId, skus: [brandProductID], square: squareImg }),
      );
      if (error) {
        console.error(error);
        return;
      }
      addProductsData({ newProducts: productResponse });
      setItemID(brandProductID);
    }
  };

  const hasBadge = [undefined, ''].includes(item?.badge) === false;

  /*
    lo hacemos así por el cambio de badge de api
    para la respuesta de unbxd solo mostramos el de camper one y el de target
    ya que no recibimos badge
  */

  let customMessage = null;

  if (withLabel === true) {
    if (hasBadge) {
      customMessage = item.badge;
    }
  }

  const priceRange =
    currentPriceData?.priceDiscountFromFormatted && currentPriceData?.priceDiscountToFormatted ?
      `${currentPriceData.priceDiscountFromFormatted} - ${currentPriceData.priceDiscountToFormatted}`
    : `${currentPriceData?.priceFromFormatted} - ${currentPriceData?.priceToFormatted}`;

  // const itemSubtitle = getDomainValue({
  //   camper: !item.recycled && STORES_WITH_SUBTITLE.includes(storeId) ? item?.subtitleH1 : null,
  //   camperlab: item?.subtitleH1,
  //   nnormal: item?.codeFeatures,
  // });

  let priceDiscountString = '';
  if (discountStr || discount) {
    priceDiscountString = discountStr || `-${discount}%`;
  }
  const priceDiscountSplitted = priceDiscountString?.split('-');
  if (priceDiscountSplitted?.length > 2) {
    const priceText = priceDiscountSplitted.pop();
    priceDiscountString = `-${priceText}`;
  }
  if (!priceDiscountString?.startsWith('-')) {
    priceDiscountString = `-${priceDiscountString}`;
  }

  const fixedPrice = getFixedPrice(market, profileData, currentPriceData?.current);

  const handleShowAddToBagPopup = (e: React.MouseEvent<HTMLButtonElement | HTMLImageElement>, mode = 'in') => {
    const onlyOneSize = item?.sizes?.length === 1;
    const outOfStock = item?.sizes?.every((size) => size.available === false) || item?.notifyMeType === 'OUT';
    const isCustomizable = item.id.includes('999');
    const notifyMeNotBuyable =
      (item?.notifyMe && item.notifyMeType === 'OUT') ||
      (item?.notifyMe && item.notifyMeType === 'SIZE' && !item.sizes?.some((size) => size.available)) === true;
    const goesStraightToPDP = outOfStock || isCustomizable || notifyMeNotBuyable || item?.membersOnly || item?.presales;

    if (!goesStraightToPDP) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      if (mode === 'out') {
        window.location.assign(`/${locale}${formatedUrl}`);
      }
      return false;
    }

    if (onlyOneSize) {
      const availableSize = item?.sizes?.[0];
      if (!availableSize) return;
      handleClickAddToCartBtn(true, availableSize.value, availableSize.label);
    } else {
      setShowingAddToBagPopup(true);
    }
  };

  const colClassName = getDomainValue({
    camper: 'relative grid-item overflow-hidden',
    nnormal: 'relative camper',
    camperlab: 'relative grid-item group overflow-hidden',
  });

  let addToBagLabel = t('ficha.producto', 'add.to.bag');

  if (item?.sizes?.every((size) => size.available === false) || item?.notifyMeType === 'OUT') {
    addToBagLabel = t('ficha.producto', 'out.of.stock');
  } else if (item.id.includes('999')) {
    addToBagLabel = t('ficha.producto', 'customize');
  } else if (item?.membersOnly) {
    addToBagLabel = `${t('ficha.producto', 'members.only.shop')} ${t('ficha.producto', 'members.only')}`;
  } else if (
    (item?.notifyMe && item.notifyMeType === 'OUT') ||
    (item?.notifyMe && item.notifyMeType === 'SIZE' && !item.sizes?.some((size) => size.available) === true)
  ) {
    addToBagLabel = t('ficha.producto', 'notify.me');
  } else if (item?.presales) {
    addToBagLabel = t('ficha.producto', 'add.to.bag.presale', 'PRE-ORDER');
  }

  const hoverComponent = getDomainValue({
    camper:
      addToBagPopup ?
        <div className="absolute bottom-0 hidden w-full opacity-0 xl:group-hover:inline xl:group-hover:opacity-100">
          <button
            className="w-full border border-dark bg-neutral-inverted p-2 text-inverted hover:bg-neutral hover:text-copy"
            onClick={handleShowAddToBagPopup}
          >
            {addToBagLabel}
          </button>
        </div>
      : showSizes && !addToBagPopup ?
        <div className="absolute bottom-0 hidden w-full opacity-0 xl:group-hover:inline xl:group-hover:opacity-100">
          <ProductCardSizes
            onSizeHover={setHoveredSize as TODO}
            template={squareImg === true ? 'square' : 'default'}
            sizes={item.sizes as TODO}
            notifyMe={item.notifyMe}
            notifyMeType={item.notifyMeType}
          />
        </div>
      : null,
    nnormal: null,
    camperlab: null,
  });

  return (
    <div onMouseDown={() => saveLastProduct && saveLastViewedProduct(id)} onMouseUp={onClickProduct} className={`${colClassName}`}>
      <div className={`${styles.productCard} nnormal:m-[4px] nnormal:border nnormal:border-clear`} data-product-id={id}>
        <ProductElement
          tabIndex={slider ? -1 : undefined}
          tag={showPrices ? 'a' : 'div'}
          className="block"
          href={!hoveredSize ? (`/${locale}${formatedUrl}` as string) : (`/${locale}${formatedUrl}?size=${hoveredSize}` as string)}
        >
          <div className={newShooting ? styles.gridPhoto : `${styles.gridPhoto} ${styles.oldShooting} oldShooting`}>
            {getDomainValue({
              camper: (
                <div className="specialProductMessage">
                  {customMessage !== null && (
                    <div key={`customMessage-${id}`} className="absolute bottom-0 left-0 z-10 text-copy lab:left-0 lab:top-2.5 sm:bottom-auto sm:top-0">
                      <p className="mb-0 px-5 py-3 text-[0.6em] uppercase lab:p-0 lab:text-xs lab:font-bold sm:text-[0.8em]">{customMessage}</p>
                    </div>
                  )}
                </div>
              ),
              nnormal: 'camper',
            })}
            <ProductCardMedia
              productId={id}
              name={name}
              title={item.subtitleH1}
              template={squareImg ? 'square' : 'default'}
              images={item.images as TODO}
              imageSources={carousel ? (item.imageSources as any) : undefined}
              imagesHover={imagesHover as TODO}
              withHover={withHover}
              index={index ?? 0}
              loading={loadingProp}
              className={`${getDomainValue({ camper: 'group', nnormal: 'group', camperlab: '' })} ${imagesClassName}`}
              sizes={hoverComponent}
              target={realTargetId}
            />
            {IS_NNORMAL && item?.webExclusive === true ?
              <p className={styles.exclusive}>{t('listado.productos', 'web.exclusive', 'exclusive nnormal.com')}</p>
            : null}
          </div>
        </ProductElement>
        <div className={`${styles.gridContent} gridContent relative lab:mx-0 lab:mt-2.5 lab:flex lab:flex-col lab:gap-2.5 ${contentClassName}`}>
          <div>
            {showColors && i18nPage === 'grid' && (
              <div className={`mb-[16px] ${styles.productColors}`}>
                <ProductBrands
                  id={id}
                  products={originalItem.colors ?? []}
                  placement={'grid'}
                  camperlab={camperlab}
                  selectBrandProduct={
                    canFetchColors ?
                      (id, products) => {
                        fetchColors(id, products);
                      }
                    : undefined
                  }
                />
              </div>
            )}
            <p className="name mb-0 text-sm nnormal:font-secondary nnormal:text-base nnormal:font-bold lab:text-xs lab:font-bold lab:uppercase">
              {name}
              {item.belowBadge ? ` - ${item.belowBadge}` : ''}
            </p>

            {withSKU && <p className={styles.sku}>{id}</p>}
            {!isGiftCard && showPrices && touristMode === false && (
              <>
                {isCalculating && <Skeleton className="h-6 w-2/3" />}
                {!isCalculating && (
                  <div className="lab:flex">
                    <p data-fp={fixedPrice} data-ge-price="data-ge-price" className={styles.price}>
                      {currentPriceData.pricePerSize ? priceRange : currentFormated}
                    </p>
                    {previous !== undefined && (
                      <p className={`${styles.discountText} discountText hidden lab:inline-flex`}>
                        <span
                          className={
                            voucherApplied && currentPriceData?.discountFromVoucher ? `${styles.discount} ${styles.discountByVoucher}` : `${styles.discount}`
                          }
                        >
                          {priceDiscountString}
                        </span>
                        <s>
                          {currentPriceData.pricePerSize ?
                            `${currentPriceData.previousFromFormatted || currentPriceData.priceFromFormatted} - ${currentPriceData.previousToFormatted || currentPriceData.priceToFormatted}`
                          : previousFormated}
                        </s>
                      </p>
                    )}
                  </div>
                )}
                {isCalculating ? null : (
                  <>
                    {previous !== undefined && (
                      <p className={`${styles.discountText} discountText lab:hidden`}>
                        <s>
                          {currentPriceData.pricePerSize ?
                            `${currentPriceData.previousFromFormatted || currentPriceData.priceFromFormatted} - ${currentPriceData.previousToFormatted || currentPriceData.priceToFormatted}`
                          : previousFormated}
                        </s>
                        <span
                          className={
                            voucherApplied && currentPriceData?.discountFromVoucher ?
                              `${styles.discount} ${styles.discountByVoucher} green`
                            : `${styles.discount} green`
                          }
                        >
                          {priceDiscountString}
                        </span>
                      </p>
                    )}
                    {previous !== undefined && voucherApplied && currentPriceData?.discountFromVoucher && voucherPriceText === null && (
                      <p className={styles.voucherApplied}>{currentPriceData.appliedLabel}</p>
                    )}
                    {![null, undefined, ''].includes(voucherPriceText) && (
                      <p className={`${styles.voucherPrice}`} dangerouslySetInnerHTML={{ __html: voucherPriceText ?? '' }} />
                    )}
                  </>
                )}
                {currentPriceData?.pricePerSize && (
                  <p className={styles.pricePerSize}>{t('listado.productos', 'final.price.acording', 'Final price acording to size')}</p>
                )}
                {installments && <p className={styles.installments}>{`${t('listado.productos', 'plazos.pago')} ${installments}`}</p>}
              </>
            )}
          </div>
          {getDomainValue({
            camper:
              addToBagPopup ?
                <div className="cursor-pointer opacity-100 xl:opacity-0">
                  <img onClick={(e) => handleShowAddToBagPopup(e, 'out')} src="/assets-new/svg/add-to-bag-plp.svg" alt="add to bag" />
                </div>
              : null,
          })}
          {getDomainValue({
            camperlab: (
              <div
                id="product-card-lab-sizes"
                key="sizes"
                className="pointer-events-none h-7 bg-neutral opacity-0 transition-opacity duration-200 ease-linear group-hover:pointer-events-auto group-hover:opacity-100"
              >
                <ProductCardSizes
                  onSizeHover={setHoveredSize as TODO}
                  template={squareImg === true ? 'square' : 'default'}
                  baseHref={`/${locale}${formatedUrl}`}
                  sizes={item.sizes as TODO}
                  notifyMe={item.notifyMe}
                  notifyMeType={item.notifyMeType}
                />
              </div>
            ),
            nnormal: 'camper' as TODO,
          })}
        </div>
      </div>
      {isDomain('camper') && showingAddToBagPopup === true && (
        <AddToBagDialog
          sku={itemID as string}
          target={realTargetId}
          data={item as unknown as TGridProduct}
          isOpen={showingAddToBagPopup}
          onClose={() => setShowingAddToBagPopup(false)}
          camperlab={camperlab}
          squareImg={squareImg}
          handleClickAddToCartBtn={handleClickAddToCartBtn}
          getDataByItemId={(idForData: string) => getSingleProductData({ id: idForData, defaultProduct: originalItem })}
        />
      )}
      {children}
    </div>
  );
}
